import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ChevronLeft, UserPlus, Send, Users, Clock, Ban, AtSign, BookOpen, Brain, FileText, ClipboardList, Sparkles } from "lucide-react";

const GroupDetail = () => {
  const { id } = useParams();
  const [g, setG] = useState(null);
  const [inviteUsername, setInviteUsername] = useState("");
  const [studyItems, setStudyItems] = useState([]);
  const [publishForm, setPublishForm] = useState({ title: "", body: "", study_item_id: "" });

  // autocomplete
  const [suggestions, setSuggestions] = useState([]);
  const [sugOpen, setSugOpen] = useState(false);
  const [sugIdx, setSugIdx] = useState(0);
  const [inviteBusy, setInviteBusy] = useState(false);
  const debounceRef = useRef(null);
  const wrapRef = useRef(null);

  // sent invites (any status)
  const [sent, setSent] = useState([]);
  const [cancelingId, setCancelingId] = useState(null);

  const load = async () => {
    const [a, b, c] = await Promise.allSettled([
      api.get(`/groups/${id}`),
      api.get("/study/items"),
      api.get(`/groups/${id}/invites`),
    ]);
    if (a.status === "fulfilled") setG(a.value.data);
    else { toast.error(a.reason?.response?.data?.detail || "Could not load this group"); setG({ error: true }); }
    if (b.status === "fulfilled") setStudyItems((b.value.data || []).filter((it) => !it.private));
    if (c.status === "fulfilled") setSent(c.value.data || []);
  };
  useEffect(() => { load(); }, [id]);

  // --- autocomplete: query /auth/users/search debounced ---
  useEffect(() => {
    const raw = inviteUsername.replace(/^@/, "").trim().toLowerCase();
    if (raw.length < 1) { setSuggestions([]); setSugOpen(false); return; }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      try {
        const { data } = await api.get("/auth/users/search", { params: { q: raw } });
        const memberIds = new Set(g?.members_info?.map((m) => m.id) || []);
        const pendingUsers = new Set(sent.filter((s) => s.status === "pending").map((s) => s.invitee_username));
        const filtered = (data || [])
          .map((u) => ({
            ...u,
            _member: memberIds.has(u.id),
            _pending: pendingUsers.has(u.username),
          }))
          .slice(0, 6);
        setSuggestions(filtered);
        setSugOpen(filtered.length > 0);
        setSugIdx(0);
      } catch { /* ignore */ }
    }, 180);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [inviteUsername, g, sent]);

  // close autocomplete on outside click
  useEffect(() => {
    const onDoc = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setSugOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const submitInvite = async (username) => {
    const uname = (username || inviteUsername).replace(/^@/, "").trim().toLowerCase();
    if (!uname) return;
    setInviteBusy(true);
    try {
      await api.post("/groups/invite", { group_id: id, username: uname });
      toast.success(`Invite sent to @${uname}`);
      setInviteUsername("");
      setSuggestions([]);
      setSugOpen(false);
      // let the sidebar count refresh once accepted; refresh sent list now
      const { data } = await api.get(`/groups/${id}/invites`);
      setSent(data || []);
      window.dispatchEvent(new Event("vertisiya:invites-changed"));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    } finally {
      setInviteBusy(false);
    }
  };

  const cancelInvite = async (inviteId, username) => {
    setCancelingId(inviteId);
    try {
      await api.post("/invites/cancel", { invite_id: inviteId, accept: false });
      toast.success(`Cancelled invite to @${username}`);
      const { data } = await api.get(`/groups/${id}/invites`);
      setSent(data || []);
      window.dispatchEvent(new Event("vertisiya:invites-changed"));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    } finally {
      setCancelingId(null);
    }
  };

  const publish = async (e) => {
    e.preventDefault();
    try {
      await api.post("/groups/publish", {
        group_id: id,
        title: publishForm.title,
        body: publishForm.body,
        study_item_id: publishForm.study_item_id || null,
      });
      toast.success("Published");
      setPublishForm({ title: "", body: "", study_item_id: "" });
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  const onInviteKey = (e) => {
    if (!sugOpen || suggestions.length === 0) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setSugIdx((i) => (i + 1) % suggestions.length); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setSugIdx((i) => (i - 1 + suggestions.length) % suggestions.length); }
    else if (e.key === "Enter") {
      const cur = suggestions[sugIdx];
      if (cur && !cur._member && !cur._pending) {
        e.preventDefault();
        submitInvite(cur.username);
      }
    } else if (e.key === "Escape") { setSugOpen(false); }
  };

  const pendingSent = useMemo(() => sent.filter((s) => s.status === "pending"), [sent]);
  const recentSent = useMemo(() => sent.filter((s) => s.status !== "pending").slice(0, 8), [sent]);

  if (!g) return <div className="p-10 text-neutral-500 text-[0.86rem]" data-testid="group-loading">Loading…</div>;
  if (g.error) return (
    <div className="p-10" data-testid="group-error">
      <Link to="/groups" className="inline-flex items-center gap-1.5 text-[0.82rem] text-neutral-500 hover:text-white mb-6"><ChevronLeft size={13}/> back to groups</Link>
      <div className="glass p-8 text-[0.9rem] text-neutral-400">This group couldn&apos;t be loaded — you may not be a member.</div>
    </div>
  );

  const statusChip = (s) => {
    const map = {
      accepted: { cls: "text-emerald-400 border-emerald-500/30 bg-emerald-500/[0.06]", label: "accepted" },
      declined: { cls: "text-neutral-400 border-white/[0.08] bg-white/[0.02]", label: "declined" },
      cancelled:{ cls: "text-neutral-500 border-white/[0.06] bg-white/[0.02]", label: "cancelled" },
      pending:  { cls: "text-amber-400 border-amber-500/30 bg-amber-500/[0.06]", label: "pending" },
    };
    const m = map[s] || map.pending;
    return <span className={`text-[9px] font-mono tracking-[0.14em] uppercase border px-1.5 py-0.5 rounded-[2px] ${m.cls}`}>{m.label}</span>;
  };

  return (
    <div className="p-6 md:p-10 max-w-6xl mx-auto">
      <Link to="/groups" className="inline-flex items-center gap-1.5 text-[0.82rem] text-neutral-500 hover:text-white transition-colors mb-8" data-testid="back-groups">
        <ChevronLeft size={13}/> back to groups
      </Link>
      <header className="mb-10">
        <div className="label-mono mb-3">/ group</div>
        <h1 className="font-display text-4xl md:text-5xl tightest font-semibold leading-none">{g.name}</h1>
        <p className="text-neutral-500 mt-3 text-[0.92rem]">{g.description || "—"}</p>
      </header>

      <div className="grid md:grid-cols-3 gap-4 mb-10">
        {/* members + invite */}
        <div className="glass p-6">
          <div className="label-mono mb-4 flex items-center gap-2">
            <Users size={11}/> members ({g.members_info?.length || 0})
          </div>
          <div className="space-y-2.5 mb-5 max-h-40 overflow-y-auto pr-1">
            {g.members_info?.map((m) => (
              <div key={m.id} className="flex items-center gap-2.5 text-[0.86rem]">
                <div className="w-7 h-7 bg-white/[0.06] border border-white/[0.08] flex items-center justify-center text-[0.72rem] rounded-[2px] text-neutral-300">
                  {m.display_name?.[0]?.toUpperCase()}
                </div>
                <div className="min-w-0">
                  <div className="text-white truncate">@{m.username}</div>
                  <div className="text-[0.72rem] text-neutral-500 truncate">{m.display_name}</div>
                </div>
              </div>
            ))}
          </div>

          <div className="label-mono mb-2">/ invite by @username</div>
          <div className="relative" ref={wrapRef}>
            <form onSubmit={(e) => { e.preventDefault(); submitInvite(); }} className="flex gap-2">
              <div className="relative flex-1">
                <AtSign size={12} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500 pointer-events-none" />
                <input
                  className="input-cyber pl-8"
                  placeholder="username"
                  value={inviteUsername}
                  onChange={(e) => setInviteUsername(e.target.value)}
                  onKeyDown={onInviteKey}
                  onFocus={() => suggestions.length > 0 && setSugOpen(true)}
                  autoComplete="off"
                  required
                  data-testid="invite-input"
                />
              </div>
              <button
                className="btn-primary px-3.5 py-2"
                type="submit"
                disabled={inviteBusy || !inviteUsername.trim()}
                data-testid="invite-send"
              >
                <UserPlus size={13}/>
              </button>
            </form>

            {sugOpen && suggestions.length > 0 && (
              <div
                className="absolute z-40 left-0 right-12 mt-1 border border-white/[0.08] rounded-[2px] bg-black/95 backdrop-blur-xl overflow-hidden shadow-[0_10px_40px_rgba(0,0,0,0.6)]"
                data-testid="invite-suggestions"
              >
                {suggestions.map((u, i) => (
                  <button
                    key={u.id}
                    type="button"
                    onMouseEnter={() => setSugIdx(i)}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => !u._member && !u._pending && submitInvite(u.username)}
                    disabled={u._member || u._pending}
                    data-testid={`invite-sug-${u.username}`}
                    className={`w-full flex items-center gap-3 px-3 py-2 text-left transition-colors ${
                      i === sugIdx && !u._member && !u._pending ? "bg-white/[0.05]" : ""
                    } ${u._member || u._pending ? "opacity-45 cursor-not-allowed" : "hover:bg-white/[0.04]"}`}
                  >
                    <div className="w-7 h-7 bg-white/[0.06] border border-white/[0.08] flex items-center justify-center text-[0.72rem] rounded-[2px] text-neutral-300 shrink-0">
                      {u.display_name?.[0]?.toUpperCase() || u.username?.[0]?.toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-[0.86rem] text-white truncate">@{u.username}</div>
                      <div className="text-[0.7rem] text-neutral-500 truncate">{u.display_name}</div>
                    </div>
                    {u._member && <span className="text-[9px] label-mono text-neutral-500">MEMBER</span>}
                    {u._pending && !u._member && <span className="text-[9px] label-mono text-amber-500">PENDING</span>}
                  </button>
                ))}
              </div>
            )}
          </div>

          {pendingSent.length > 0 && (
            <div className="mt-5 pt-4 border-t border-white/[0.06]">
              <div className="label-mono mb-2 flex items-center gap-2">
                <Clock size={11}/> pending sent ({pendingSent.length})
              </div>
              <div className="space-y-1.5 max-h-44 overflow-y-auto pr-1" data-testid="pending-sent-list">
                {pendingSent.map((iv) => (
                  <div key={iv.id} className="flex items-center justify-between gap-2 py-1.5" data-testid={`pending-invite-${iv.invitee_username}`}>
                    <div className="min-w-0 flex-1">
                      <div className="text-[0.82rem] text-white truncate">@{iv.invitee_username}</div>
                      <div className="text-[0.7rem] text-neutral-500 truncate">sent {relTime(iv.created_at)}</div>
                    </div>
                    <button
                      onClick={() => cancelInvite(iv.id, iv.invitee_username)}
                      disabled={cancelingId === iv.id}
                      className="text-neutral-500 hover:text-rose-400 transition-colors shrink-0 p-1 disabled:opacity-40"
                      title="Cancel invite"
                      data-testid={`cancel-invite-${iv.invitee_username}`}
                    >
                      <Ban size={13}/>
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {recentSent.length > 0 && (
            <div className="mt-4 pt-4 border-t border-white/[0.06]">
              <div className="label-mono mb-2">/ recent</div>
              <div className="space-y-1 max-h-32 overflow-y-auto pr-1">
                {recentSent.map((iv) => (
                  <div key={iv.id} className="flex items-center justify-between gap-2 py-1 text-[0.78rem]">
                    <div className="text-neutral-400 truncate">@{iv.invitee_username}</div>
                    {statusChip(iv.status)}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* publish */}
        <div className="glass p-6 md:col-span-2">
          <div className="label-mono mb-4">/ publish</div>
          <form onSubmit={publish} className="space-y-3">
            <input className="input-cyber" placeholder="Title" value={publishForm.title} onChange={(e)=>setPublishForm({...publishForm, title:e.target.value})} required data-testid="publish-title"/>
            <textarea className="input-cyber min-h-[100px] resize-y" placeholder="Share what you learned…" value={publishForm.body} onChange={(e)=>setPublishForm({...publishForm, body:e.target.value})} data-testid="publish-body"/>
            <select className="input-cyber" value={publishForm.study_item_id} onChange={(e)=>setPublishForm({...publishForm, study_item_id:e.target.value})} data-testid="publish-item">
              <option value="">(no attached revision material)</option>
              {studyItems.map((it) => (
                <option key={it.id} value={it.id}>{niceKind(it.kind)} · {it.subject || "Untitled"}</option>
              ))}
            </select>
            <button type="submit" className="btn-primary" data-testid="publish-submit"><Send size={13}/> Publish</button>
          </form>
        </div>
      </div>

      <section>
        <div className="label-mono mb-3">/ feed</div>
        <h2 className="font-display text-xl tracking-tight mb-4">Recent posts</h2>
        <div className="space-y-3">
          {g.posts?.length === 0 && <div className="text-neutral-500 text-[0.86rem]">Be the first to share something.</div>}
          {g.posts?.map((p) => (
            <div key={p.id} className="glass p-6" data-testid={`post-${p.id}`}>
              <div className="label-mono mb-3 flex items-center gap-2">
                <span className="text-white">@{p.author_username}</span>
                <span>·</span>
                <span>{new Date(p.created_at).toLocaleString()}</span>
              </div>
              <div className="font-medium text-[0.95rem] mb-1.5">{p.title}</div>
              {p.body && <p className="text-neutral-300 text-[0.86rem] mt-2 whitespace-pre-wrap leading-relaxed">{p.body}</p>}
              {p.snapshot && <SharedMaterial snapshot={p.snapshot} />}
            </div>
          ))}
        </div>
      </section>
    </div>
  );
};

function kindIcon(kind) {
  const map = { notes: FileText, study_guide: BookOpen, flashcards: Brain, practice_test: ClipboardList, worksheet: ClipboardList, study_plan: Sparkles };
  return map[kind] || FileText;
}

// Renders an attached revision material (or legacy recording) inside the group feed.
const SharedMaterial = ({ snapshot }) => {
  const [open, setOpen] = useState(false);
  if (!snapshot) return null;

  // legacy: a raw recording was attached
  if (!snapshot.kind) {
    return (
      <div className="mt-4 border border-white/[0.08] rounded-[2px] p-4" data-testid="shared-recording">
        <div className="label-mono mb-1">/ recording</div>
        <div className="text-[0.86rem] text-white">{snapshot.subject || "Untitled"}</div>
        {snapshot.transcript && (
          <p className="text-[0.8rem] text-neutral-400 mt-2 line-clamp-3 leading-relaxed">{snapshot.transcript}</p>
        )}
      </div>
    );
  }

  const Icon = kindIcon(snapshot.kind);
  const cards = snapshot.data?.cards || snapshot.data?.flashcards;
  const questions = snapshot.data?.questions;
  const count = Array.isArray(cards) ? cards.length : Array.isArray(questions) ? questions.length : null;

  return (
    <div className="mt-4 border border-white/[0.10] bg-white/[0.02] rounded-[2px]" data-testid="shared-material">
      <div className="flex items-center gap-3 px-4 py-3 border-b border-white/[0.06]">
        <div className="w-8 h-8 border border-amber-500/30 bg-amber-500/[0.06] flex items-center justify-center rounded-[2px] shrink-0">
          <Icon size={13} className="text-amber-400" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[0.86rem] text-white truncate">{niceKind(snapshot.kind)}</div>
          <div className="text-[0.7rem] text-neutral-500 truncate">
            {snapshot.subject || "Untitled"}{count !== null ? ` · ${count} ${Array.isArray(cards) ? "cards" : "questions"}` : ""}
          </div>
        </div>
        <button onClick={() => setOpen((o) => !o)} className="btn-secondary text-[0.72rem] py-1.5 px-3 shrink-0" data-testid="shared-material-toggle">
          {open ? "Hide" : "Open"}
        </button>
      </div>

      {open && (
        <div className="p-4 max-h-96 overflow-y-auto" data-testid="shared-material-body">
          {Array.isArray(cards) && (
            <div className="space-y-2">
              {cards.map((c, i) => (
                <div key={i} className="border border-white/[0.06] rounded-[2px] p-3">
                  <div className="text-[0.84rem] text-white">{c.front}</div>
                  {c.front_en && <div className="text-[0.74rem] text-neutral-500 mt-0.5">{c.front_en}</div>}
                  <div className="text-[0.82rem] text-neutral-300 mt-2">{c.back}</div>
                  {c.back_en && <div className="text-[0.74rem] text-neutral-500 mt-0.5">{c.back_en}</div>}
                </div>
              ))}
            </div>
          )}
          {Array.isArray(questions) && (
            <ol className="space-y-3 list-decimal pl-5">
              {questions.map((q, i) => (
                <li key={i} className="text-[0.84rem] text-neutral-200">
                  <div>{q.q}</div>
                  {q.q_en && <div className="text-[0.74rem] text-neutral-500">{q.q_en}</div>}
                  {Array.isArray(q.choices) && (
                    <ul className="mt-1 space-y-0.5 text-[0.78rem] text-neutral-400">
                      {q.choices.map((ch, j) => <li key={j}>· {ch}</li>)}
                    </ul>
                  )}
                </li>
              ))}
            </ol>
          )}
          {!Array.isArray(cards) && !Array.isArray(questions) && (
            <pre className="text-[0.8rem] text-neutral-300 whitespace-pre-wrap font-sans leading-relaxed">{snapshot.content}</pre>
          )}
        </div>
      )}
    </div>
  );
};

function niceKind(kind) {
  const map = { notes: "Notes", study_guide: "Study guide", flashcards: "Flashcards", practice_test: "Practice test", worksheet: "Worksheet", study_plan: "Study plan" };
  return map[kind] || kind || "Study item";
}

// tiny relative time (avoids extra deps)
function relTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 86400 * 7) return `${Math.floor(diff / 86400)}d ago`;
  return d.toLocaleDateString();
}

export default GroupDetail;
